/**
 * Task Repository
 * 
 * Handles workflow task operations with GraphQL backend
 */

import { BaseRepository, FilterParams, PaginationParams, SortParams } from './BaseRepository';

export interface Task {
    id: string;
    instanceId: string;
    name: string;
    description?: string;
    taskType: 'user' | 'system' | 'approval' | 'review' | 'escalation';
    status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled' | 'overdue';
    priority: 'low' | 'normal' | 'high' | 'urgent';
    assignedTo?: string;
    assignedTeam?: string;
    dueDate?: string;
    startedAt?: string;
    completedAt?: string;
    estimatedDuration?: number;
    actualDuration?: number;
    dependencies?: string[];
    context: Record<string, any>;
    formSchema?: Record<string, any>;
    formData?: Record<string, any>;
    approvalRequired?: boolean;
    escalationRules?: EscalationRule[];
    metadata?: Record<string, any>;
}

export interface EscalationRule {
    id: string;
    trigger: 'overdue' | 'stale' | 'priority';
    condition: Record<string, any>;
    action: 'reassign' | 'notify' | 'escalate_priority';
    escalateTo?: string;
    delayMinutes?: number;
}

export interface TaskMetrics {
    totalTasks: number;
    pendingTasks: number;
    inProgressTasks: number;
    completedTasks: number;
    overdueTasks: number;
    avgCompletionTime: number;
    // DRR-114: rate metrics are T3 QAPI — access via QAPI-scoped query only
}

class TaskRepository extends BaseRepository<Task> {
    protected entityName = 'Task';

    // Find all tasks with optional filtering
    async findAll(params?: {
        filter?: FilterParams;
        pagination?: PaginationParams;
        sort?: SortParams;
    }): Promise<Task[]> {
        const query = `
            query ListTasks($filter: TaskFilterInput, $pagination: PaginationInput, $sort: SortInput) {
                tasks(filter: $filter, pagination: $pagination, sort: $sort) {
                    id
                    instanceId
                    name
                    description
                    taskType
                    status
                    priority
                    assignedTo
                    assignedTeam
                    dueDate
                    startedAt
                    completedAt
                    estimatedDuration
                    actualDuration
                    dependencies
                    context
                    formSchema
                    formData
                    approvalRequired
                    escalationRules {
                        id
                        trigger
                        condition
                        action
                        escalateTo
                        delayMinutes
                    }
                    metadata
                }
            }
        `;

        const variables = {
            filter: params?.filter || {},
            pagination: params?.pagination || {},
            sort: params?.sort || {}
        };

        const result = await this.query<{ tasks: Task[] }>(query, variables);
        return result.tasks || [];
    }

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Task | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Task',
            criticalityLevel: 'MEDIUM',
            metadata: { id, taskRepository: true }
        });

        const query = `
            query GetTaskByTenantAndId($id: String!, $tenant: String!) {
                task(id: $id, tenant: $tenant) {
                    id
                    instanceId
                    name
                    description
                    taskType
                    status
                    priority
                    assignedTo
                    assignedTeam
                    dueDate
                    startedAt
                    completedAt
                    estimatedDuration
                    actualDuration
                    dependencies
                    context
                    formSchema
                    formData
                    approvalRequired
                    escalationRules {
                        id
                        trigger
                        condition
                        action
                        escalateTo
                        delayMinutes
                    }
                    metadata
                }
            }
        `;

        try {
            const result = await this.query<{ task: Task | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 90 * 1000 // 90 seconds cache for tasks (shorter than instances)
            });

            const task = result.task || null;

            // ✅ FERP SAFETY: Log successful operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Task',
                    entityId: id,
                    found: !!task,
                    taskStatus: task?.status,
                    taskType: task?.taskType,
                    assignedTo: task?.assignedTo,
                    timestamp: Date.now()
                });
            }

            return task;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Task',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Find task by ID (legacy method - redirects to findByTenantAndId)
    async findById(taskId: string): Promise<Task | null> {
        return this.findByTenantAndId(this.tenant, taskId);
    }

    // Find tasks by assignee
    async findByAssignee(assigneeId: string): Promise<Task[]> {
        const query = `
            query GetTasksByAssignee($assigneeId: String!) {
                tasksByAssignee(assigneeId: $assigneeId) {
                    id
                    instanceId
                    name
                    description
                    taskType
                    status
                    priority
                    assignedTo
                    assignedTeam
                    dueDate
                    startedAt
                    completedAt
                    estimatedDuration
                    actualDuration
                    dependencies
                    context
                    formSchema
                    formData
                    approvalRequired
                    escalationRules {
                        id
                        trigger
                        condition
                        action
                        escalateTo
                        delayMinutes
                    }
                    metadata
                }
            }
        `;

        const result = await this.query<{ tasksByAssignee: Task[] }>(query, { assigneeId });
        return result.tasksByAssignee || [];
    }

    // Create a new task
    async create(taskData: Partial<Task>): Promise<Task> {
        const mutation = `
            mutation CreateTask($input: TaskInput!) {
                createTask(input: $input) {
                    id
                    instanceId
                    name
                    description
                    taskType
                    status
                    priority
                    assignedTo
                    assignedTeam
                    dueDate
                    estimatedDuration
                    dependencies
                    context
                    formSchema
                    approvalRequired
                    escalationRules {
                        id
                        trigger
                        condition
                        action
                        escalateTo
                        delayMinutes
                    }
                    metadata
                }
            }
        `;

        const result = await this.mutation<{ createTask: Task }>(mutation, { input: taskData });
        return result.createTask;
    }

    // Update task
    async update(taskId: string, updateData: Partial<Task>): Promise<Task> {
        const mutation = `
            mutation UpdateTask($id: String!, $input: TaskUpdateInput!) {
                updateTask(id: $id, input: $input) {
                    id
                    instanceId
                    name
                    description
                    taskType
                    status
                    priority
                    assignedTo
                    assignedTeam
                    dueDate
                    startedAt
                    completedAt
                    estimatedDuration
                    actualDuration
                    dependencies
                    context
                    formSchema
                    formData
                    approvalRequired
                    escalationRules {
                        id
                        trigger
                        condition
                        action
                        escalateTo
                        delayMinutes
                    }
                    metadata
                }
            }
        `;

        const result = await this.mutation<{ updateTask: Task }>(mutation, {
            id: taskId,
            input: updateData
        });
        return result.updateTask;
    }

    // Start task
    async start(taskId: string): Promise<Task> {
        const mutation = `
            mutation StartTask($id: String!) {
                startTask(id: $id) {
                    id
                    status
                    startedAt
                }
            }
        `;

        const result = await this.mutation<{ startTask: Task }>(mutation, { id: taskId });
        return result.startTask;
    }

    // Complete task
    async complete(taskId: string, formData?: Record<string, any>, comment?: string): Promise<Task> {
        const mutation = `
            mutation CompleteTask($id: String!, $formData: JSON, $comment: String) {
                completeTask(id: $id, formData: $formData, comment: $comment) {
                    id
                    status
                    completedAt
                    actualDuration
                    formData
                }
            }
        `;

        const result = await this.mutation<{ completeTask: Task }>(mutation, {
            id: taskId,
            formData,
            comment
        });
        return result.completeTask;
    }

    // Reassign task
    async reassign(taskId: string, newAssignee: string, reason?: string): Promise<Task> {
        const mutation = `
            mutation ReassignTask($id: String!, $newAssignee: String!, $reason: String) {
                reassignTask(id: $id, newAssignee: $newAssignee, reason: $reason) {
                    id
                    assignedTo
                }
            }
        `;

        const result = await this.mutation<{ reassignTask: Task }>(mutation, {
            id: taskId,
            newAssignee,
            reason
        });
        return result.reassignTask;
    }

    // Escalate task
    async escalate(taskId: string, escalateTo: string, reason: string): Promise<Task> {
        const mutation = `
            mutation EscalateTask($id: String!, $escalateTo: String!, $reason: String!) {
                escalateTask(id: $id, escalateTo: $escalateTo, reason: $reason) {
                    id
                    assignedTo
                    priority
                }
            }
        `;

        const result = await this.mutation<{ escalateTask: Task }>(mutation, {
            id: taskId,
            escalateTo,
            reason
        });
        return result.escalateTask;
    }

    // Delete task
    async delete(taskId: string): Promise<boolean> {
        const mutation = `
            mutation DeleteTask($id: String!) {
                deleteTask(id: $id)
            }
        `;

        const result = await this.mutation<{ deleteTask: boolean }>(mutation, { id: taskId });
        return result.deleteTask;
    }

    // Get task metrics
    async getMetrics(options?: {
        assignedTo?: string;
        instanceId?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<TaskMetrics> {
        const query = `
            query GetTaskMetrics($assignedTo: String, $instanceId: String, $startDate: String, $endDate: String) {
                taskMetrics(assignedTo: $assignedTo, instanceId: $instanceId, startDate: $startDate, endDate: $endDate) {
                    totalTasks
                    pendingTasks
                    inProgressTasks
                    completedTasks
                    overdueTasks
                    avgCompletionTime
                }
            }
        `;

        const result = await this.query<{ taskMetrics: TaskMetrics }>(query, options || {});
        return result.taskMetrics;
    }
}

// Create and export instance
export const taskRepository = new TaskRepository();
export { TaskRepository };
export default TaskRepository;